import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Circle, X } from 'lucide-react';
import { API } from '../App';
import { useT } from '../i18n';
import ConversionCheck from './ConversionCheck';
import QuickSetup from './QuickSetup';

/*
  First-day checklist (plan, stage 1). Progress comes from facts in the
  workspace (GET /api/users/me/first-day), not from wizard clicks: source ->
  offer -> campaign -> stream with an offer -> first click -> first
  conversion. Your own domain is optional and last. While the Stripe trial
  runs, the card also says when the card is charged and what happens without
  payment.
*/

const HIDE_KEY = 'xp_first_day_hidden';

function stepText(t, key) {
  switch (key) {
    case 'source':
      return [t('Traffic source'), t('Where visitors come from: an ad network or a website. The source template adds the right parameters to your link.')];
    case 'offer':
      return [t('Offer'), t('The partner page the visitor is sent to. In the offer, connect the affiliate network so conversions come back to the tracker.')];
    case 'campaign':
      return [t('Campaign'), t('Gives you the link for your ads and ties the source to the offer.')];
    case 'stream':
      return [t('Stream with an offer'), t('Decides where each visitor goes. Without a stream, clicks go to the default URL of the campaign.')];
    case 'click':
      return [t('First click'), t('Open your campaign link once or start the ad. To see where a visitor would go without opening the link, use the route check.')];
    case 'conversion':
      return [t('First conversion'), t('The affiliate network reports each conversion to the tracker (postback). Put the postback URL of the campaign into your account at the network.')];
    default:
      return [key, ''];
  }
}

function stepActions(t, key, campaignId) {
  const camp = campaignId ? `/campaigns/${campaignId}` : '/campaigns';
  switch (key) {
    case 'source':
      return [{ to: '/traffic-sources', label: t('Add a source') }];
    case 'offer':
      return [{ to: '/offers', label: t('Add an offer') }];
    case 'campaign':
      return [{ to: '/campaigns', label: t('Create a campaign') }];
    case 'stream':
      return [{ to: campaignId ? `${camp}/streams` : '/campaigns', label: t('Add a stream') }];
    case 'click':
      return [
        ...(campaignId ? [{ dialog: true, label: t('Check click and conversion') }] : []),
        ...(campaignId ? [{ to: `${camp}/simulate`, label: t('Check the route') }] : []),
        { to: '/clicks/log', label: t('Open the click log') },
      ];
    case 'conversion':
      return [
        ...(campaignId ? [{ dialog: true, label: t('Check click and conversion') }] : []),
        { to: camp, label: t('Postback URL') },
      ];
    default:
      return [];
  }
}

export default function FirstDayChecklist() {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [checkOpen, setCheckOpen] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [checkCampaign, setCheckCampaign] = useState(null);
  const [hidden, setHidden] = useState(() => {
    try {
      return localStorage.getItem(HIDE_KEY) === '1';
    } catch (_e) {
      return false;
    }
  });

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users/me/first-day`);
      setData(res.data);
    } catch (_e) {
      setData(null); // the checklist is a helper: no error toast on the dashboard
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!data) return null;
  const trial = data.trial;
  // Done and no trial countdown to show: nothing left to say.
  if (data.complete && !trial) return null;
  if (data.complete && hidden) return null;

  const hide = () => {
    try {
      localStorage.setItem(HIDE_KEY, '1');
    } catch (_e) {
      /* storage disabled */
    }
    setHidden(true);
  };

  // The trial end and charge are shown by the banner on every page (TrialBanner).

  return (
    <section className="mb-6 rounded-lg border bg-card p-5 text-card-foreground" data-testid="first-day-checklist">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold">
            {data.complete ? t('The campaign works: the first click and the first conversion have arrived.') : t('First day: what is left to set up')}
          </h2>
          {!data.complete ? (
            <p className="mt-1 text-sm text-muted-foreground">
              {t('Each step takes a few minutes. When the first click and the first conversion arrive, the campaign works.')}{' '}
              <span className="font-medium text-foreground">{t('{done} of {total} done', { done: data.done, total: data.total })}</span>
            </p>
          ) : null}
          {!data.complete && !(data.steps || []).find((s) => s.key === 'campaign' && s.done) ? (
            <button type="button" className="xp-btn xp-btn-primary mt-3" onClick={() => setSetupOpen(true)}>
              {t('Set up step by step')}
            </button>
          ) : null}
        </div>
        {data.complete ? (
          <button
            type="button"
            onClick={hide}
            aria-label={t('Hide')}
            className="rounded p-1 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      {!data.complete ? (
        <ol className="space-y-3">
          {(data.steps || []).map((s) => {
            const [title, hint] = stepText(t, s.key);
            const isNext = s.key === data.next;
            return (
              <li key={s.key} className={`flex items-start gap-3 ${isNext ? '' : 'opacity-80'}`}>
                {s.done ? (
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-label={t('Done')} />
                ) : (
                  <Circle className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                )}
                <div className="min-w-0 flex-1">
                  <div className={`text-sm ${isNext ? 'font-semibold' : 'font-medium'}`}>{title}</div>
                  {!s.done ? <div className="text-sm text-muted-foreground">{hint}</div> : null}
                  {isNext ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {stepActions(t, s.key, data.campaign_id).map((a) =>
                        a.dialog ? (
                          <button
                            key={a.label}
                            type="button"
                            onClick={() => setCheckOpen(true)}
                            className="xp-btn xp-btn-primary"
                          >
                            {a.label}
                          </button>
                        ) : (
                          <Link key={a.to + a.label} to={a.to} className="xp-btn">
                            {a.label}
                          </Link>
                        )
                      )}
                    </div>
                  ) : null}
                </div>
              </li>
            );
          })}
          <li className="flex items-start gap-3 opacity-80">
            {data.domain ? (
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-label={t('Done')} />
            ) : (
              <Circle className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium">{t('Your own domain (optional)')}</div>
              {!data.domain ? (
                <div className="text-sm text-muted-foreground">
                  {t('Links on your own domain. DNS records take up to 24 hours to update, so you can do this later.')}{' '}
                  <Link to="/domains" className="underline hover:text-foreground">
                    {t('Add a domain')}
                  </Link>
                </div>
              ) : null}
            </div>
          </li>
        </ol>
      ) : null}

      <QuickSetup
        open={setupOpen}
        onOpenChange={(value) => {
          setSetupOpen(value);
          if (!value) load();
        }}
        onCheck={(id) => {
          setSetupOpen(false);
          setCheckCampaign(id);
          setCheckOpen(true);
          load();
        }}
      />
      {checkCampaign || data.campaign_id ? (
        <ConversionCheck
          campaignId={checkCampaign || data.campaign_id}
          open={checkOpen}
          onOpenChange={(value) => {
            setCheckOpen(value);
            if (!value) load();
          }}
        />
      ) : null}
    </section>
  );
}
